import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Loader2 } from 'lucide-react';
import * as api from '../lib/api';
import { useAuthStore, homePathFor } from '../store/useAuthStore';

const MIN_LENGTH = 10;

/**
 * Forced password change for an account someone else created.
 *
 * There is no mailer, so a new staff member is handed their first password by
 * voice or chat. That credential has to stop working the moment it has been
 * used, which is why this screen is the only thing the account can reach until
 * it is done.
 */
export default function SetPasswordPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const refresh = useAuthStore((s) => s.bootstrap);

  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;
  const tooShort = form.next.length > 0 && form.next.length < MIN_LENGTH;
  const ready = form.current && form.next.length >= MIN_LENGTH && form.next === form.confirm;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.changePassword(form.current, form.next);
      // Re-read the session so must_change_password clears and the guard stops
      // sending us back here.
      await refresh();
      navigate(homePathFor(user?.role), { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-brand-50">
          <KeyRound className="h-5 w-5 text-brand-700" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Choose your password</h1>
        <p className="mt-1.5 text-sm text-slate-600">
          This account was set up for you with a temporary password. Pick your own before you
          carry on — the temporary one stops working as soon as you do.
        </p>

        {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

        <form onSubmit={submit} className="mt-5">
          <label className="field-label mb-1" htmlFor="current">Temporary password</label>
          <input id="current" type="password" autoComplete="current-password" required autoFocus
                 className="field-input" value={form.current} onChange={set('current')} />
          <p className="mt-1 text-xs text-slate-500">Hyphens are only for reading — type it with or without them.</p>

          <label className="field-label mb-1 mt-4" htmlFor="next">New password</label>
          <input id="next" type="password" autoComplete="new-password" required minLength={MIN_LENGTH}
                 className="field-input" value={form.next} onChange={set('next')}
                 aria-describedby="next-hint" aria-invalid={tooShort || undefined} />
          <p id="next-hint" className={`mt-1 text-xs ${tooShort ? 'text-rose-700' : 'text-slate-500'}`}>
            At least {MIN_LENGTH} characters.
          </p>

          <label className="field-label mb-1 mt-4" htmlFor="confirm">Confirm new password</label>
          <input id="confirm" type="password" autoComplete="new-password" required
                 className="field-input" value={form.confirm} onChange={set('confirm')}
                 aria-invalid={mismatch || undefined} />
          {mismatch && <p className="mt-1 text-xs text-rose-700">These do not match.</p>}

          <button type="submit" disabled={!ready || busy}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Set password and continue
          </button>
        </form>
      </div>
    </div>
  );
}
